import React from "react";

import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  Divider,
  IconButton
} from "@mui/material";

import HomeIcon from "@mui/icons-material/Home";
import FlightIcon from "@mui/icons-material/Flight";
import CloseIcon from "@mui/icons-material/Close";

export default function MatchDetails({ game, onBack }) {
  if (!game) return null;

  const warriorsHome = game.home_team === "Warriors";

  const warriorsScore = warriorsHome
    ? game.home_team_score
    : game.away_team_score;

  const opponentScore = warriorsHome
    ? game.away_team_score
    : game.home_team_score;

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });

  const formatTime = (dateStr) =>
    new Date(dateStr).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

  const getResult = () => {
    if (warriorsScore > opponentScore) {
      return { label: "WIN", color: "#00E676" };
    }
    if (warriorsScore < opponentScore) {
      return { label: "LOSS", color: "#FF5252" };
    }
    return { label: "DRAW", color: "#A7B3C2" };
  };

  const result = getResult();

  return (
    <Card
      sx={{
        width: "100%",
        maxWidth: 380,
        height: 390,
        margin: "0 auto",
        borderRadius: 3,
        background: "linear-gradient(145deg, #0B2A57, #123C73)",
        color: "#F5F7FA",
        boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography
            sx={{
              fontWeight: 800,
              letterSpacing: 1,
              fontSize: "1rem",
            }}
          >
            MATCH DETAILS
          </Typography>

          <IconButton
            onClick={onBack}
            size="small"
            sx={{
              backgroundColor: "#00A651",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#008C45",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Teams + Score */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          {/* Team */}
          <Box textAlign="center" flex={1}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: "#00A651",
                margin: "0 auto 6px auto",
              }}
            >
              {warriorsHome ? (
                <HomeIcon fontSize="small" />
              ) : (
                <FlightIcon fontSize="small" />
              )}
            </Avatar>

            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
              }}
            >
              Warriors vs {warriorsHome ? game.away_team : game.home_team}
            </Typography>
          </Box>

          {/* Score */}
          <Box textAlign="center" flex={1}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
              }}
            >
              <Typography sx={{ fontSize: "1.8rem", fontWeight: 900 }}>
                {game.home_team_score}
              </Typography>

              <Typography sx={{ fontSize: "1rem", color: "#A7B3C2" }}>
                -
              </Typography>

              <Typography sx={{ fontSize: "1.8rem", fontWeight: 900 }}>
                {game.away_team_score}
              </Typography>
            </Box>

            <Chip
              label={result.label}
              size="small"
              sx={{
                mt: 1,
                backgroundColor: result.color,
                color: "#fff",
                fontWeight: 700,
                fontSize: "0.65rem",
                height: 20,
              }}
            />
          </Box>
        </Box>

        <Divider
          sx={{
            my: 2,
            borderColor: "rgba(255,255,255,0.08)",
          }}
        />

        {/* Match Info */}
        <Box display="flex" flexDirection="column" gap={1.5}>
          {/* Venue */}
          <Box sx={infoBoxStyle}>
            <Typography sx={labelStyle}>VENUE</Typography>
            <Typography sx={valueStyle}>{game.location}</Typography>
          </Box>

          {/* Date */}
          <Box sx={infoBoxStyle}>
            <Typography sx={labelStyle}>DATE</Typography>
            <Typography sx={valueStyle}>
              {formatDate(game.game_date)}
            </Typography>
          </Box>

          {/* Time */}
          <Box sx={infoBoxStyle}>
            <Typography sx={labelStyle}>KICKOFF</Typography>
            <Typography sx={valueStyle}>
              {formatTime(game.game_date)}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

/* reusable styles */
const infoBoxStyle = {
  background: "rgba(255,255,255,0.04)",
  borderRadius: 2,
  p: 1.5,
};

const labelStyle = {
  color: "#A7B3C2",
  fontSize: "0.65rem",
  fontWeight: 700,
  letterSpacing: 1,
  mb: 0.3,
};

const valueStyle = {
  fontWeight: 700,
  fontSize: "0.85rem",
};